/** Map staff investment API payloads to Investment Hub UI shapes. */

import { convertToTitleCase } from "./helpers";
import { normalizeTimelineItems } from "./timeline";

export function mapStaffOpportunityListItem(item) {
  if (!item) return null;
  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    category: item.category,
    providerName: item.provider_name,
    riskLevel: item.risk_level,
    status: item.status,
    unitPriceNgn: Number(item.unit_price) || 0,
    expectedReturnPct: Number(item.expected_return_percentage) || 0,
    durationMonths: item.duration_months,
    totalUnits: item.total_units,
    availableUnits: item.available_units,
    fundedAmountNgn: Number(item.funded_amount) || 0,
    fundingTargetNgn: Number(item.funding_target) || 0,
    fundingCloseDate: item.funding_close_date,
    maturityDate: item.maturity_date,
    participantCount: item.participant_count ?? 0,
    publishedAt: item.published_at,
    createdByName: item.created_by_name,
    updatedAt: item.updated_at,
    canAcceptRequests: Boolean(item.can_accept_requests),
    notReadyReason: item.not_ready_reason || null,
    suggestedAction: item.suggested_action
      ? {
          apiAction: item.suggested_action.api_action || null,
          uiAction: item.suggested_action.ui_action || null,
          label: item.suggested_action.label,
        }
      : null,
  };
}

export function mapStaffOpportunityDetail(item) {
  const base = mapStaffOpportunityListItem(item);
  if (!base) return null;

  const content = item.public_content || {};

  return {
    ...base,
    branchId: item.branch_id,
    shortDescription: item.short_description,
    fullDescription: item.full_description,
    currency: item.currency || "NGN",
    minUnits: item.minimum_units,
    maxUnits: item.maximum_units,
    payoutType: item.payout_type,
    fundingOpenDate: item.funding_open_date,
    investmentStartDate: item.investment_start_date,
    maturityDate: item.maturity_date,
    returnPayoutDate: item.payout_date,
    coverImageUrl: item.cover_image,
    overview: content.overview || item.short_description,
    fundsUsage: content.fundsUsage,
    highlights: content.highlights || [],
    howItWorks: content.howItWorks || [],
    gallery: content.gallery || [],
    milestones: content.milestones || [],
    faqs: content.faqs || [],
    terms: content.terms,
    riskDisclaimer: content.riskDisclaimer,
    settings: item.settings || {},
    scheduledPublishAt: item.settings?.scheduled_publish_date || "",
    updates: (item.updates || []).map(mapStaffOpportunityUpdate),
    documents: (item.documents || []).map(mapStaffOpportunityDocument),
    fundedUnits: (item.total_units || 0) - (item.available_units || 0),
  };
}

export function mapStaffInvestmentRequestListItem(item) {
  if (!item) return null;
  return {
    id: item.id,
    reference: item.request_reference,
    opportunityId: item.opportunity_id,
    opportunityTitle: item.opportunity_title,
    participantType:
      item.account_type === "member" ? "cooperative_member" : "standalone_investor",
    applicantId: item.applicant_id,
    applicantName: item.applicant_name,
    participantName: item.applicant_name,
    email: item.email,
    phone: item.phone,
    participantEmail: item.email,
    participantPhone: item.phone,
    units: item.units,
    unitPriceNgn: Number(item.unit_price) || 0,
    totalNgn: Number(item.total_amount) || 0,
    amountPaid: item.amount_paid != null ? Number(item.amount_paid) : null,
    isPaid: Boolean(item.is_paid || item.paid_at),
    paidAt: item.paid_at,
    paymentReference: item.payment_reference,
    status: item.status,
    kycStatus: item.kyc_status || "none",
    submittedAt: item.submitted_at,
    reviewedBy: item.reviewed_by,
    reviewedByName: item.reviewed_by_name || item.reviewed_by,
  };
}

export function mapStaffInvestmentRequestDetail(item) {
  const base = mapStaffInvestmentRequestListItem(item);
  if (!base) return null;

  return {
    ...base,
    expectedReturnPct: Number(item.expected_return_snapshot) || 0,
    projectedPayoutNgn: Number(item.projected_payout_snapshot) || 0,
    feeAmount: item.fee_amount != null ? Number(item.fee_amount) : null,
    feeBreakdown: item.fee_breakdown || null,
    fullName: item.full_name,
    contactMethod: item.contact_method,
    note: item.note,
    staffNotes: Array.isArray(item.staff_notes)
      ? item.staff_notes
      : item.staff_notes
        ? [{ text: item.staff_notes, by: item.reviewed_by_name || "Staff", at: item.reviewed_at }]
        : [],
    reviewedAt: item.reviewed_at,
    allocatedAt: item.allocated_at,
    maturedAt: item.matured_at,
    cancelledAt: item.cancelled_at,
    timeline: normalizeTimelineItems(item.timeline),
  };
}

export function mapStaffOpportunityUpdate(item) {
  if (!item) return null;
  return {
    id: item.id,
    title: item.title,
    body: item.body,
    status: item.status,
    visibility: item.visibility,
    imageUrl: item.image,
    publishedAt: item.published_at,
    createdAt: item.created_at,
    investorVisible: item.visibility === "public" && item.status === "published",
  };
}

export function mapStaffOpportunityDocument(item) {
  if (!item) return null;
  return {
    id: item.id,
    title: item.title,
    fileType: item.document_type || item.mime_type || "—",
    fileName: item.file_name,
    fileSizeKb: item.file_size ? Math.round(item.file_size / 1024) : null,
    url: item.file_path || item.file_url || null,
    visibility: item.visibility,
    sortOrder: item.sort_order ?? 0,
  };
}

export function mapStaffInvestmentRequestToParticipant(item, opportunity = null) {
  const mapped = mapStaffInvestmentRequestListItem(item);
  if (!mapped) return null;

  const paymentStatus =
    mapped.isPaid || mapped.status === "allocated"
      ? "paid"
      : mapped.status === "pending_payment"
        ? "pending_payment"
        : mapped.status === "approved"
          ? "pending_payment"
          : "not_applicable";

  return {
    id: mapped.id,
    reference: mapped.reference,
    name: mapped.participantName,
    participantType: mapped.participantType,
    units: mapped.units,
    committedAmount: mapped.totalNgn,
    requestStatus: mapped.status,
    kycStatus: mapped.kycStatus,
    paymentStatus,
    nextPayoutDate: opportunity?.maturityDate || opportunity?.returnPayoutDate || null,
    date: mapped.submittedAt,
    requestId: mapped.reference || mapped.id,
  };
}

export function mapStaffParticipantListItem(item) {
  if (!item) return null;
  return {
    participantId: item.participant_id,
    name: item.name,
    participantType: item.account_type === "member" ? "cooperative_member" : "standalone_investor",
    email: item.email,
    kycStatus: item.kyc_status || "none",
    opportunityCount: item.opportunity_count ?? 0,
    totalCommitted: Number(item.total_requested) || 0,
    approvedValue: Number(item.approved_amount) || 0,
    allocatedValue: Number(item.allocated_amount) || 0,
    projectedReturns: Number(item.projected_returns) || 0,
    activeInvestments: item.active_investments ?? 0,
    nextMaturity: item.next_maturity,
    latestActivityAt: item.latest_activity_at,
    requests: (item.request_ids || []).map((id) => ({ id })),
  };
}

export function mapStaffDashboardSummary(data) {
  if (!data) return null;
  return {
    totalFundsRequested: data.total_requested_value,
    approvedInvestmentValue: data.approved_value,
    allocatedValue: data.allocated_value,
    expectedReturnExposure: data.projected_return_exposure,
    activeOpportunities: data.active_opportunities,
    totalParticipants: data.total_participants,
    requestsAwaitingReview: data.requests_awaiting_review,
    closingSoon: Array.from({ length: data.opportunities_closing_soon || 0 }),
    upcomingMaturities: Array.from({ length: data.upcoming_maturities || 0 }),
    fundingOverview: (data.funding_overview
      ? [{
          id: "summary",
          title: "Portfolio",
          fundedAmountNgn: data.funding_overview.funded_amount,
          fundingTargetNgn: data.funding_overview.funding_target,
          availableUnits: data.funding_overview.available_units,
        }]
      : []),
    recentActivity: (data.recent_activity || []).map((row) => ({
      id: row.id,
      reference: row.reference,
      status: row.status,
      amount: row.amount,
      opportunityTitle: row.opportunity_title,
      at: row.at,
    })),
    needsAttention: [],
  };
}

export function mapStaffOpportunityFormToApi(values) {
  const emptyToNull = (v) => (v === "" || v === undefined || v === null || Number.isNaN(v) ? null : v);
  const numOrNull = (v) => {
    if (v === "" || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  return {
    title: values.title,
    category: emptyToNull(values.category),
    short_description: emptyToNull(values.shortDescription),
    full_description: emptyToNull(values.fullDescription),
    provider_name: emptyToNull(values.providerName),
    risk_level: String(values.riskLevel || "moderate").toLowerCase(),
    unit_price: numOrNull(values.unitPriceNgn),
    total_units: numOrNull(values.totalUnits),
    minimum_units: numOrNull(values.minUnits),
    maximum_units: numOrNull(values.maxUnits),
    expected_return_percentage: numOrNull(values.expectedReturnPct),
    duration_months: numOrNull(values.durationMonths),
    payout_type: emptyToNull(values.payoutType),
    funding_open_date: emptyToNull(values.fundingOpenDate),
    funding_close_date: emptyToNull(values.fundingCloseDate),
    investment_start_date: emptyToNull(values.investmentStartDate),
    maturity_date: emptyToNull(values.maturityDate),
    payout_date: emptyToNull(values.returnPayoutDate),
    scheduled_publish_date: emptyToNull(values.scheduledPublishAt),
    cover_image: emptyToNull(values.coverImageUrl),
    public_content: {
      overview: values.overview,
      fundsUsage: values.fundsUsage,
      highlights: values.highlights,
      howItWorks: values.howItWorks,
      gallery: values.gallery,
      milestones: values.milestones,
      faqs: values.faqs,
      terms: values.terms,
      riskDisclaimer: values.riskDisclaimer,
    },
  };
}

export const OPPORTUNITY_TARGET_TO_API_ACTION = {
  internal_review: "submit-review",
  scheduled: "schedule",
  published: "publish",
  draft: "unpublish",
  funding: "open-funding",
  fully_funded: "mark-fully-funded",
  active: "activate",
  matured: "mark-matured",
  closed: "close",
  archived: "archive",
};
